// Pure helpers for the "Customer Performance" drill-down page opened from the
// Tiered Customer Performance dashboard widget. Kept free of React/redux so the
// slug→param mapping, the date range, and the response flattening can be unit
// tested in isolation.

// Each Tiered Customer Performance row maps to a route slug and the exact
// GetOrderChangeHistory query params that return that row's underlying orders.
// (Derived from the live ForecastTest/GetOrderChangeHistory taxonomy.)
export const CUSTOMER_PERFORMANCE_ITEMS = {
  "uncovered-labor": {
    title: "Uncovered Labor",
    reason: "Uncovered",
    changeCategory: "All",
  },
  "uncovered-material-stockout": {
    title: "Uncovered Material Stockout",
    reason: "Material",
    changeCategory: "All",
  },
  qualities: {
    title: "Qualities",
    reason: "All",
    changeCategory: "Quality",
  },
  shortages: {
    title: "Shortages",
    reason: "All",
    changeCategory: "Shortage",
  },
  "customer-requested": {
    title: "Customer Requested",
    reason: "All",
    changeCategory: "customerRequested",
  },
  "AddOn": {
    title: "Add-Ons",
    reason: "AddOn",
    changeCategory: "All",
  },
};

// Resolve a route slug to its { title, reason, changeCategory } config, or null.
export const resolveItem = (slug) =>
  (slug && CUSTOMER_PERFORMANCE_ITEMS[slug]) || null;

const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
const toYmd = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// Year-to-date range: Jan 1 of the given day's year → the given day, both as
// YYYY-MM-DD (matches the widget's YTD column and the API's date format).
export const getYtdRange = (now = new Date()) => ({
  fromDate: `${now.getFullYear()}-01-01`,
  toDate: toYmd(now),
});

// Normalize a date value (ISO datetime from the widget API, or a plain date)
// to the YYYY-MM-DD string GetOrderChangeHistory expects. Returns "" if absent.
export const toApiDate = (value) => {
  if (!value) return "";
  const s = String(value);
  const isoDay = s.match(/^\d{4}-\d{2}-\d{2}/);
  if (isoDay) return isoDay[0];
  const d = new Date(s);
  return isNaN(d.getTime()) ? "" : toYmd(d);
};

// GetOrderChangeHistory returns result[0] = { rescheduling, orderQuality,
// orderShortage }. The query params already scope which group is populated, so
// flattening every list gives exactly the rows for the selected item.
export const flattenChangeHistory = (data) => {
  const result = data && Array.isArray(data.result) ? data.result : [];
  const top = result[0] && typeof result[0] === "object" ? result[0] : {};
  return ["rescheduling", "orderQuality", "orderShortage", "orderAddOn"].reduce(
    (rows, key) => (Array.isArray(top[key]) ? rows.concat(top[key]) : rows),
    []
  );
};

// Human-readable "Reason / Status" for a change-history row across all groups.
export const getRowReason = (row) => {
  if (!row) return "—";
  if (row.reason) return row.reason;
  const selected = Array.isArray(row.reasons)
    ? row.reasons.filter((r) => r && r.isSelected).map((r) => r.reason)
    : [];
  if (selected.length) return selected.join(", ");
  if (row.qualityStatus) return row.qualityStatus;
  return "—";
};
